// components/CreateAssetModal.jsx
import React, { useState, useEffect } from "react";
import { X, Package, Loader } from "lucide-react";
import apiService from "../services/api";
import { useTheme } from "../contexts/ThemeContext";

const CreateAssetModal = ({ account, onClose, onSuccess }) => {
  const { isDarkMode } = useTheme();
  const [formData, setFormData] = useState({
    accountId: account?.id || null,
    productId: null,
    assetName: "",
    quantity: 1,
    purchaseDate: null,
    installDate: null,
    status: "Requested",
    description: null,
    orderNotes: null,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [products, setProducts] = useState([]);
  const [productSearch, setProductSearch] = useState("");
  const [showProductDropdown, setShowProductDropdown] = useState(false);

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showProductDropdown && !event.target.closest(".product-dropdown")) {
        setShowProductDropdown(false);
        setProductSearch("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showProductDropdown]);

  const fetchProducts = async () => {
    try {
      const response = await apiService.getProducts();
      setProducts(response.data || []);
    } catch (err) {
      console.error("Error fetching products:", err);
    }
  };

  const statusOptions = ["Requested", "Ordered", "Original Sale", "Add-on"];

  const filteredProducts = products.filter((product) => {
    if (!productSearch) return true;
    const searchLower = productSearch.toLowerCase();
    return (
      product.productName?.toLowerCase().includes(searchLower) ||
      product.productCode?.toLowerCase().includes(searchLower) ||
      product.productDescription?.toLowerCase().includes(searchLower)
    );
  });

  const selectedProduct = products.find((p) => p.id === formData.productId);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const submitData = {
        ...formData,
        productId: formData.productId || null,
        purchaseDate: formData.purchaseDate || null,
        installDate: formData.installDate || null,
        description: formData.description?.trim() || null,
        orderNotes: formData.orderNotes?.trim() || null,
      };

      await apiService.createAsset(submitData);
      onSuccess();
    } catch (err) {
      console.error("Error creating asset:", err);
      setError(err.response?.data?.message || "Failed to create asset");
    } finally {
      setLoading(false);
    }
  };

  // Theme helpers
  const inputClass = `w-full px-4 py-2.5 border rounded-xl focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-200 ${
    isDarkMode
      ? "bg-[#0C0E12] border-gray-700/50 text-white placeholder-gray-500"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
  }`;

  return (
    <div className="fixed inset-0 bg-black/75 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
      <div
        className={`border rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col ${
          isDarkMode
            ? "bg-gradient-to-br from-[#13161B] to-[#0C0E12] border-gray-800/50"
            : "bg-white border-gray-200"
        }`}
      >
        {/* Header */}
        <div
          className={`border-b p-6 ${
            isDarkMode
              ? "border-gray-800/50 bg-gradient-to-br from-[#0C0E12] to-[#0A0C10]"
              : "border-gray-200 bg-gray-50"
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
                <Package size={20} className="text-blue-400" />
              </div>
              <h2
                className={`text-xl font-bold ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                New Asset
              </h2>
            </div>
            <button
              onClick={onClose}
              className={`rounded-lg p-2 transition-all duration-200 ${
                isDarkMode
                  ? "text-gray-400 hover:text-white hover:bg-gray-800/50"
                  : "text-gray-500 hover:text-gray-900 hover:bg-gray-100"
              }`}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm animate-slideDown">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-6">
              <div
                className={`flex items-center gap-3 pb-4 border-b ${
                  isDarkMode ? "border-gray-800/50" : "border-gray-200"
                }`}
              >
                <div className="w-1 h-5 bg-blue-500 rounded-full"></div>
                <h3
                  className={`text-base font-semibold ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Asset Information
                </h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    <span className="text-red-400">*</span> Asset Name
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.assetName}
                    onChange={(e) =>
                      setFormData({ ...formData, assetName: e.target.value })
                    }
                    className={inputClass}
                    placeholder="Enter asset name"
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Quantity
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.quantity}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        quantity: parseInt(e.target.value) || 1,
                      })
                    }
                    className={inputClass}
                  />
                </div>

                <div className="relative product-dropdown">
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Product
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={
                        showProductDropdown
                          ? productSearch
                          : selectedProduct?.productName || ""
                      }
                      onChange={(e) => {
                        setProductSearch(e.target.value);
                        setShowProductDropdown(true);
                      }}
                      onFocus={() => setShowProductDropdown(true)}
                      placeholder="Search products..."
                      className={inputClass}
                    />
                    {showProductDropdown && (
                      <div
                        className={`absolute z-10 w-full mt-2 border rounded-xl shadow-2xl max-h-60 overflow-y-auto ${
                          isDarkMode
                            ? "bg-[#0C0E12] border-gray-700/50"
                            : "bg-white border-gray-200"
                        }`}
                      >
                        {filteredProducts.length > 0 ? (
                          filteredProducts.map((product) => (
                            <div
                              key={product.id}
                              onClick={() => {
                                setFormData({
                                  ...formData,
                                  productId: product.id,
                                });
                                setShowProductDropdown(false);
                                setProductSearch("");
                              }}
                              className={`px-4 py-3 cursor-pointer transition-colors ${
                                isDarkMode
                                  ? "hover:bg-gray-800 text-white"
                                  : "hover:bg-gray-50 text-gray-900"
                              }`}
                            >
                              <div className="font-medium text-sm">
                                {product.productName}
                              </div>
                              {product.productCode && (
                                <div
                                  className={`text-xs mt-0.5 ${
                                    isDarkMode
                                      ? "text-gray-400"
                                      : "text-gray-500"
                                  }`}
                                >
                                  {product.productCode}
                                </div>
                              )}
                            </div>
                          ))
                        ) : (
                          <div
                            className={`px-4 py-3 text-sm ${
                              isDarkMode ? "text-gray-500" : "text-gray-400"
                            }`}
                          >
                            No products found
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  {selectedProduct && !showProductDropdown && (
                    <button
                      type="button"
                      onClick={() => {
                        setFormData({ ...formData, productId: null });
                        setProductSearch("");
                      }}
                      className={`absolute right-3 top-10 transition-colors duration-200 ${
                        isDarkMode
                          ? "text-gray-500 hover:text-white"
                          : "text-gray-400 hover:text-gray-700"
                      }`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Purchase Date
                  </label>
                  <input
                    type="date"
                    value={formData.purchaseDate || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        purchaseDate: e.target.value || null,
                      })
                    }
                    className={inputClass}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Account
                  </label>
                  <div
                    className={`w-full px-4 py-2.5 border border-gray-700/50 rounded-xl flex items-center ${
                      isDarkMode
                        ? "bg-gray-800/30 text-gray-400"
                        : "bg-gray-100 border-gray-300 text-gray-500"
                    }`}
                  >
                    <span className="mr-2">🏢</span>
                    {account?.name || "No account selected"}
                  </div>
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Install Date
                  </label>
                  <input
                    type="date"
                    value={formData.installDate || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        installDate: e.target.value || null,
                      })
                    }
                    className={inputClass}
                  />
                </div>

                <div className="md:col-span-2">
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) =>
                      setFormData({ ...formData, status: e.target.value })
                    }
                    className={inputClass}
                  >
                    {statusOptions.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Description
                  </label>
                  <textarea
                    rows="3"
                    value={formData.description || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        description: e.target.value || null,
                      })
                    }
                    className={`${inputClass} resize-none`}
                    placeholder="Enter description"
                  />
                </div>

                <div className="md:col-span-2">
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Order Notes
                  </label>
                  <textarea
                    rows="3"
                    value={formData.orderNotes || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        orderNotes: e.target.value || null,
                      })
                    }
                    className={`${inputClass} resize-none`}
                    placeholder="Enter order notes"
                  />
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Footer Actions */}
        <div
          className={`border-t p-6 ${
            isDarkMode
              ? "border-gray-800/50 bg-gradient-to-br from-[#0C0E12] to-[#0A0C10]"
              : "border-gray-200 bg-gray-50"
          }`}
        >
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className={`flex-1 px-5 py-3 border rounded-xl font-medium transition-all duration-200 ${
                isDarkMode
                  ? "bg-gray-800/50 hover:bg-gray-800 border-gray-700/50 hover:border-gray-600/50 text-gray-300 hover:text-white"
                  : "bg-white hover:bg-gray-100 border-gray-300 text-gray-700"
              }`}
            >
              Cancel
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={loading}
              className="flex-1 px-5 py-3 text-white rounded-xl font-semibold transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-blue-600/20 hover:shadow-blue-600/30"
              style={{
                background:
                  "radial-gradient(50% 50% at 50% 50%, #427BCA 0%, #2F578F 100%)",
              }}
            >
              {loading ? (
                <>
                  <Loader className="animate-spin" size={18} />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Package size={18} />
                  <span>Create Asset</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
        .animate-slideDown {
          animation: slideDown 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default CreateAssetModal;
